import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import IconZoomMinus from '@assets/images/icons/zoom_minus.svg'
import IconZoomPlus from '@assets/images/icons/zoom_plus.svg'
import IconDownload from '@assets/images/icons/download.svg'
import ReloadIconSvg from '@assets/images/icons/reload.svg'
import NoReceiptSvg from '@assets/images/no_receipt.svg'
import Panel from '@/components/layouts/panel/index'
import PanelHeader from '@/components/layouts/panel/panel-header'
import { DEBOUNCE_TIMES, DocumentControlButton, SquareIconLink } from '@/modules/base/components/buttons/index'
import PanelContent from '@/components/layouts/panel/panel-content'
import PanelFileContent from '@/modules/file/components/panels/panel-file-content'
import Carousel from '@/modules/base/components/carousel/carousel'
import ReceiptPreviewerItem from '@/modules/receipt/preview/item/receipt-previewer-item'
import { toast } from '@/modules/base/components/toast/toastConfig'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { Title } from '@/modules/base/components/texts'
// FLAG:useDocuAi — artifact switcher (original/XML/validation report/report/attachments) for e-invoices
// whose DMS document has children; flag-OFF this import is never evaluated at render time.
import { user } from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'
import InvoiceArtifactPreviewForFile from '@/modules/document/components/review/invoice-artifact-preview-for-file'

const GLOBAL_ROTATE_DEFAULT = 0

const useStyles = makeStyles(theme => ({
  panelBackground: {
    background: theme.white,
    padding: 0,
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 80,
    width: '100%',
    backgroundColor: theme.white,
    gap: 20,
    '& a': {
      border: theme.border,
      backgroundColor: theme.white,
      minWidth: 45,
      minHeight: 45,
    },
    '& a:hover': {
      backgroundColor: theme.backgroundColor,
    },
  },
  button: {
    margin: 0,
    height: 40,
    maxHeight: 40,
    width: 40,
    maxWidth: 40,
    transition: '.2s',
  },
  emptyState: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    fontWeight: theme.fontWeights.medium,
    gap: 15,
  },
  previewer: ({ withoutToolbar }) => ({
    ...(withoutToolbar && {
      flex: 1,
    }),
  }),
}))

const ExpenseAndRevenueDetailPreview = (props) => {
  const classes = useStyles(props)
  const { onZoom, zoom = 1, file, classNames, downloadLabel, downloadUrl, metaData, downloadDisabled, classNamePreviewHeader, withoutToolbar = false, onActiveFileChange } = props
  const ROTATE_DEFAULT = useMemo(() => metaData && metaData.rotate ? metaData.rotate * 1 : GLOBAL_ROTATE_DEFAULT, [])
  const [rotate, setRotate] = useState(ROTATE_DEFAULT)
  const [zoomInternal, setZoomInternal] = useState(zoom)
  const [filesInternal, setFilesInternal] = useState([])
  // FLAG:useDocuAi — which of the shown receipts switched to the artifact preview. The legacy
  // toolbar below belongs to the slide that is ON SCREEN, because the artifact preview carries its
  // own zoom/rotate/download controls while a plain photo slide still needs the legacy ones.
  const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const [artifactPreviewFileIds, setArtifactPreviewFileIds] = useState([])
  // Which slide the carousel is showing. Reported by `onSlideChange` below and reset with the file
  // set, so it can never point past the end of `filesInternal`.
  const [activeIndex, setActiveIndex] = useState(0)

  const rotateRight = () => {
    const degree = (rotate >= 270) ? 0 : rotate + 90
    setRotate(degree)
  }

  useEffect(() => {
    if (onZoom) {
      setZoomInternal(zoom)
    }
  }, [zoom])

  useEffect(() => {
    Promise.resolve(file).then((files) => {
      const normalizedFiles = Array.isArray(files) ? files : files ? [files] : []
      setFilesInternal(normalizedFiles)
      // The carousel starts over on a new file set, and so must the index the toolbar decision reads.
      setActiveIndex(0)
      onActiveFileChange?.(normalizedFiles[0] ?? null)
    }).catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }, [file, onActiveFileChange])

  const onZoomInternal = (zoomStep) => {
    if (onZoom) {
      onZoom(zoomStep)
    } else {
      setZoomInternal(currentZoom => Math.max(currentZoom + zoomStep, 0.1))
    }
  }

  // FLAG:useDocuAi — a slide reports whether it resolved to the artifact switcher; the ids drive
  // the toolbar decision below. Keyed by file id so re-reports (re-fetch, file swap) stay idempotent.
  const onArtifactPreviewResolved = (fileId, hasArtifacts) => {
    setArtifactPreviewFileIds(current => {
      const without = current.filter(id => id !== fileId)
      return hasArtifacts ? [...without, fileId] : without
    })
  }

  const renderLegacyItem = (receipt) => (
    <ReceiptPreviewerItem
      isFile
      disableActions
      key={receipt.id}
      item={receipt}
      scale={zoomInternal}
      rotate={rotate}
      size={null} />
  )

  const renderCarousel = () => {
    if (filesInternal?.length === 0) {
      return (
        <div className={classes.emptyState}>
          <SVGIcon svg={NoReceiptSvg} height='188px' />
          <Title size='medium16'>{lang.noReceipt}</Title>
        </div>
      )
    }

    return (
      <Carousel
        dots={true}
        infinite={false}
        draggable={false}
        speed={500}
        slidesToShow={1}
        slidesToScroll={1}
        dotsClass={classes.carouselDots}
        customClass={classes.previewer}
        settings={{
          onSlideChange: swiper => {
            setActiveIndex(swiper.activeIndex)
            onActiveFileChange?.(filesInternal[swiper.activeIndex] ?? null)
          },
        }}
      >
        {filesInternal?.map((receipt, i) => (
          <PanelContent key={`receipt${i}`}>
            <PanelFileContent className={classes.panelBackground}>
              {/* eslint-disable-next-line multiline-ternary */}
              {useDocuAiEnabled ? (
                /* FLAG:useDocuAi — e-invoices with children render the artifact switcher; every
                   other file falls through to the identical legacy item below. */
                <InvoiceArtifactPreviewForFile
                  fileId={receipt.fileId || receipt.id}
                  onResolved={hasArtifacts => onArtifactPreviewResolved(receipt.fileId || receipt.id, hasArtifacts)}>
                  {renderLegacyItem(receipt)}
                </InvoiceArtifactPreviewForFile>
              ) : renderLegacyItem(receipt)}
            </PanelFileContent>
          </PanelContent>

        ))}
      </Carousel>
    )
  }

  const hasNoFiles = filesInternal?.length === 0

  // FLAG:useDocuAi — drop the legacy zoom/rotate/download bar for the slide the user is LOOKING at,
  // when that slide switched to the artifact preview (which draws its own controls). Requiring
  // EVERY file to have switched meant a mixed Vorgang (e-invoice + Kassenbon) never dropped the bar,
  // so on the e-invoice slide the artifact preview's own side toolbar and this top bar were both on
  // screen — two toolbars for one document, and a Vorgang with a single e-invoice behaved
  // differently from the same e-invoice next to a receipt photo. A plain photo slide still keeps
  // the bar, which is the reason the decision is per slide and not per Vorgang.
  // Flag-OFF the id list stays empty, so this is always false.
  const activeFile = filesInternal?.[activeIndex]
  const hidesLegacyToolbar = !!activeFile
    && artifactPreviewFileIds.includes(activeFile.fileId || activeFile.id)

  return (
    <div className={classNames}>
      <Panel>
        {withoutToolbar || hidesLegacyToolbar
          ? null
          : (
            <PanelHeader toolbarRender={() => (
              <div className={clsx(classNamePreviewHeader, classes.previewHeader)}>
                <DocumentControlButton
                  icon={IconZoomMinus}
                  title={lang.expense.previewToolbar.items.zoomOut}
                  customClass={classes.button}
                  debounceTime={DEBOUNCE_TIMES.OFF}
                  onClick={() => onZoomInternal(-0.1)}
                  disabled={hasNoFiles} />
                <DocumentControlButton
                  icon={IconZoomPlus}
                  title={lang.expense.previewToolbar.items.zoomIn}
                  customClass={classes.button}
                  debounceTime={DEBOUNCE_TIMES.OFF}
                  onClick={() => onZoomInternal(+0.1)}
                  disabled={hasNoFiles} />
                <DocumentControlButton
                  title={lang.expense.previewToolbar.items.rotate90}
                  icon={ReloadIconSvg}
                  customClass={classes.button}
                  debounceTime={DEBOUNCE_TIMES.OFF}
                  onClick={rotateRight}
                  disabled={hasNoFiles} />
                {downloadDisabled
                  ? null
                  : <SquareIconLink
                    customClass={classes.button}
                    icon={IconDownload}
                    tooltip={downloadLabel}
                    href={downloadUrl}
                    target='_blank'
                    disabled={hasNoFiles} />}
              </div>
            )} />
          )}
        {renderCarousel()}
      </Panel>
    </div>
  )
}

ExpenseAndRevenueDetailPreview.propTypes = {
  file: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]).isRequired,
  zoom: PropTypes.number,
  classNames: PropTypes.string,
  classNamePreviewHeader: PropTypes.string,
  onZoom: PropTypes.func.isRequired,
  withoutToolbar: PropTypes.bool,
  onActiveFileChange: PropTypes.func,
}

export default ExpenseAndRevenueDetailPreview
